import * as THREE from 'three';
import { bendsLayout, chapelLayout, hexToWorld, lanternAt, streetLayout, type BattleState, type BendsLayout, type ChapelLayout, type MapDef, type StreetLayout } from '@somnhold/sim';
import { Kit, place } from '../seasons/kit.ts';
import type { OmenCtx } from '../omens/common.ts';
import { StreetCraft } from './camino-street.ts';
import { WORLD_PER_HEX } from './coords.ts';
import { GlowLayer } from './glow.ts';
import { SkyDome, SnowFall, smoothTiles } from './snow.ts';
import type { TerrainBuild } from './terrain.ts';

/**
 * The cleric's last road, chapter 1 (docs/sendas/clerigo.md, «La camilla»): the south face of the Spine at night,
 * Musgoviejo's lower street, the shepherds' terraces and the hairpin bends up to the cliff road. Snow falls slowly
 * everywhere; it is white and warm inside the stretcher lantern's circle and dark blue out of it (the path's contrast:
 * the circle is also the rule, the stalkers show only inside it, so its edge is drawn as a faint ring).
 *
 * - `musgoStreet` (act 1) and `musgoNight` (the opening's cinematic): the lane winding up between shut houses (timber
 *   and pale plaster under snowy slate, their shutters closed, a cold window here and there), the alleys between them,
 *   the little square at the foot with its trough, the village's arch at the top with a shrine lamp.
 * - `crossChapel` (act 2) and `chapelFire` (the campfire): the terraces, their low dry-stone walls under snow; the chapel
 *   at the top (pale stone, a steep slate roof, its door under the lamp); the two hooks on their iron posts, the two
 *   mirrors on their stands (a disc of polished steel in an iron yoke that turns), Mother Aud's inscription by the
 *   first; the beam as a strip of white light on the snow from the lantern to where it stops, a glint at each mirror;
 *   the lamp lit, a flash, and the door swinging open with the light inside.
 * - `lapaSlope` (act 3, the test) and `slopeTop` (the close): the way in hairpins up the hill, the hill between its legs
 *   in snowy terraces of rock rising to the top, cairns at the bends and the ink on the snow where the limpets come out;
 *   the cliff road at the top with its waymark.
 * - `musgoHall` (the opening): the mayor's hall, timber walls, the long table, the hearth's red, candles.
 *
 * Draw calls: the dressing (one merged mesh), the lantern's pool and its edge (two discs), the beam (one strip mesh,
 * rebuilt only when it changes), the mirrors, the hooks' lantern and the door (a few groups), the glow layer, the snow
 * (points moved in the shader) and the sky. No lights of its own: the board's are repainted (HOURS).
 */

export const CAMINO_PLACES = new Set(['musgoStreet', 'musgoNight', 'musgoHall', 'crossChapel', 'chapelFire', 'lapaSlope', 'slopeTop']);

const S = WORLD_PER_HEX;
const K = S / 1000;
const WHITE = new THREE.Color('#f4f8ff');
const WARM = new THREE.Color('#ffe2c4');
const COLD = new THREE.Color('#cfdcff');
const RED = new THREE.Color('#ff4f3a');
const INK = new THREE.Color('#6a4aa8');
const HEARTH = new THREE.Color('#ff7a4a');
const PLASTER = ['#857f76', '#7a756e', '#8f897f'];
const TIMBER = ['#261f1a', '#30281f', '#1f1a16'];
const SLATE = ['#3a4049', '#323840', '#424852'];
const SNOW = ['#c8d3e1', '#bccadb', '#d3dcea'];
/** Roofs: slate, old thatch gone dark, slate patched with wood. */
const ROOF = ['#2e333b', '#3a3129', '#353b44', '#2b2622', '#40464f'];
const STONE = ['#6c7079', '#61656e', '#767a83'];
const DRY = ['#555a62', '#4c5159', '#5e636b'];
const PINE = ['#2f3f36', '#2a3a31', '#34453b'];

export type Hour = 'day' | 'dusk' | 'night' | 'dawn';

/** The light by the hour: the south face at night is blue, not black (the snow holds the moon). */
export const HOURS: Record<Hour | 'inside', { bg: string; fog: [string, number, number]; hemi: [string, string, number]; key: [string, number]; rim: [string, number]; sky: [string, string, string] }> = {
  day: { bg: '#9fb0c2', fog: ['#9fb0c2', 30, 120], hemi: ['#dbe4f0', '#2a2d34', 2.2], key: ['#fff6ee', 2.8], rim: ['#a9bfee', 1.4], sky: ['#5f7896', '#c9d5e2', '#ffffff'] },
  dawn: { bg: '#5a6a84', fog: ['#5e6c86', 30, 120], hemi: ['#c8d2e4', '#22252c', 2.0], key: ['#ffe6dc', 2.4], rim: ['#a9bfee', 1.5], sky: ['#1e2a44', '#8a98b4', '#ffb39a'] },
  dusk: { bg: '#3d475b', fog: ['#46506a', 28, 120], hemi: ['#c3cfe2', '#1c1f27', 2.6], key: ['#ffe2d4', 3.0], rim: ['#9ab4f0', 2.0], sky: ['#18203a', '#6a7694', '#ff9a7a'] },
  night: { bg: '#0b111d', fog: ['#0f1727', 26, 95], hemi: ['#7d92c0', '#10131c', 1.3], key: ['#aabce6', 0.95], rim: ['#7f9bff', 1.1], sky: ['#03060d', '#132036', '#a9bcef'] },
  inside: { bg: '#07080b', fog: ['#0a0b0f', 18, 52], hemi: ['#c0b4aa', '#1a1412', 2.5], key: ['#e6dcd2', 1.5], rim: ['#ff8a5a', 0.7], sky: ['#000000', '#000000', '#000000'] },
};

/* ---------- the scenes' boards ---------- */

const allCells = (R: number): { q: number; r: number }[] => {
  const out: { q: number; r: number }[] = [];
  for (let r = -R; r <= R; r++) for (let q = -R; q <= R; q++) if (Math.max(Math.abs(q), Math.abs(r), Math.abs(q + r)) <= R) out.push({ q, r });
  return out;
};

/** The scenes' own boards (game/cine.ts): a straight lane, the hall, the chapel's terrace, the top of the slope. */
export const caminoSceneMap = (place0: string): MapDef | null => {
  if (!CAMINO_PLACES.has(place0) || place0 === 'musgoStreet' || place0 === 'crossChapel' || place0 === 'lapaSlope') return null;
  const R = 6;
  const rocks = allCells(R).filter((h) => {
    const p = hexToWorld(h.q, h.r);
    const d = Math.max(Math.abs(h.q), Math.abs(h.r), Math.abs(h.q + h.r));
    if (place0 === 'musgoNight') return Math.abs(p.x) > 1600;
    if (place0 === 'musgoHall') return d >= 6;
    if (place0 === 'chapelFire') return h.r <= -3 && Math.abs(p.x) <= 2600;
    return h.r >= 3;
  });
  return { radius: R + 2, bastion: R, sectors: [], kind: 'incursion', open: true, rocks };
};

/* ---------- the layer ---------- */

interface MirrorView {
  g: THREE.Group;
  disc: THREE.Group;
  yaw: number;
  want: number;
  at: THREE.Vector3;
  spin: number;
}

export class CaminoLayer {
  readonly group = new THREE.Group();
  protected mats: THREE.Material[] = [];
  protected geos: THREE.BufferGeometry[] = [];
  protected glow = new GlowLayer(180);
  protected dome: SkyDome | null = null;
  protected fall: SnowFall | null = null;
  protected terrain: TerrainBuild | null = null;
  protected inside: boolean;
  protected now = 0;
  protected lights: { p: THREE.Vector3; c: THREE.Color; size: number; a: number; seed: number; flick: number }[] = [];
  // The lantern's pool on the ground and the edge of its light.
  protected pool: THREE.Mesh | null = null;
  protected edge: THREE.Mesh | null = null;
  protected poolMat: THREE.ShaderMaterial | null = null;
  protected edgeMat: THREE.ShaderMaterial | null = null;
  protected lamp: THREE.Group | null = null;
  // The chapel.
  protected C: ChapelLayout | null = null;
  protected mirrors: MirrorView[] = [];
  protected beam: THREE.Mesh | null = null;
  protected beamMat: THREE.ShaderMaterial | null = null;
  protected beamKey = '';
  protected door: THREE.Group | null = null;
  protected doorOpen = 0;
  protected doorWant = 0;
  protected lampLit = 0;
  protected lampAt: THREE.Vector3 | null = null;
  protected hookTop: THREE.Vector3[] = [];
  protected flash = 0;
  // The street, the slope.
  protected St: StreetLayout | null = null;
  protected B: BendsLayout | null = null;
  protected stains: THREE.Vector3[] = [];
  protected lastState: BattleState | null = null;
  protected v3 = new THREE.Vector3();

  constructor(
    protected ctx: OmenCtx,
    protected map: MapDef,
    readonly place: string,
    s: BattleState,
  ) {
    this.group.name = `camino-${place}`;
    this.inside = place === 'musgoHall';
    const kit = new Kit();
    // (A later chapter's own place: engine/nieve.ts builds it on this layer's craft.)
    if (this.buildOwn(kit, s)) {
      // built
    } else if (place === 'musgoStreet') this.buildStreet(kit, s);
    else if (place === 'musgoNight') this.buildSceneStreet(kit);
    else if (place === 'crossChapel') this.buildChapel(kit, s);
    else if (place === 'chapelFire') this.buildChapelScene(kit);
    else if (place === 'lapaSlope') this.buildSlope(kit, s);
    else if (place === 'slopeTop') this.buildSlopeTop(kit);
    else this.buildHall(kit);
    if (!this.inside) this.beyond(kit);
    const mat = new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.88, metalness: 0.04, flatShading: true });
    this.mats.push(mat);
    const dress = kit.build(mat, 'camino-dress');
    this.streetCraft?.dispose();
    this.streetCraft = null;
    if (dress) {
      dress.castShadow = true;
      dress.receiveShadow = true;
      this.geos.push(dress.geometry);
      this.group.add(dress);
    }
    if (s.rules?.senda?.litter) this.buildPool(this.place === 'musgoStreet' ? 0.36 : 0.22);
    this.group.add(this.glow.mesh);
  }

  /** A subclass's own place (true: it built it). Called from the constructor: a subclass keeps its state in `declare` fields. */
  protected buildOwn(_kit: Kit, _s: BattleState): boolean {
    return false;
  }

  /** A subclass's own glows each frame (inside the glow layer's begin and end). */
  protected frameOwn(_now: number, _dt: number): void {}

  /** Past the board: the village's roofs going on (the street), and the south face's pines under snow round it all. */
  protected beyond(kit: Kit): void {
    const R = this.map.radius;
    const street = this.place === 'musgoStreet' || this.place === 'musgoNight';
    const cone = new THREE.ConeGeometry(0.5, 1, 7);
    for (let r = -(R + 9); r <= R + 9; r++)
      for (let q = -(R + 9); q <= R + 9; q++) {
        const d = Math.max(Math.abs(q), Math.abs(r), Math.abs(q + r));
        if (d <= R - 1 || d > R + 8) continue;
        const k = this.k(q, r, 11);
        const p = this.hex(q, r);
        if (street && d <= R + 1) {
          if (k > 0.45) this.craft(kit).back(p.clone().setY(p.y - 0.15), (Math.floor(k * 6) % 6) * (Math.PI / 3), k, this.k(q, r, 23));
          continue;
        }
        if (k < 0.42) continue;
        const h = 1.8 + k * 2.0;
        const x = p.x + (k - 0.5) * 0.8;
        const z = p.z + (this.k(q, r, 12) - 0.5) * 0.8;
        this.pine(kit, cone, x, p.y, z, h, k);
      }
    cone.dispose();
  }

  /* ---------- helpers ---------- */

  protected track<T extends THREE.Material | THREE.BufferGeometry>(x: T): T {
    if (x instanceof THREE.Material) this.mats.push(x);
    else this.geos.push(x);
    return x;
  }

  protected hex(q: number, r: number, h = 0): THREE.Vector3 {
    const c = hexToWorld(q, r);
    return this.ctx.at(c.x, c.y, h);
  }

  protected light(p: THREE.Vector3, color: THREE.Color, size: number, a: number, flick = 0): void {
    this.lights.push({ p: p.clone(), c: color.clone(), size, a, seed: this.lights.length * 1.37, flick });
  }

  /** A deterministic jitter for a cell (looks only). */
  protected k(q: number, r: number, salt = 0): number {
    return Math.sin(q * 12.9898 + r * 78.233 + salt * 37.7) * 0.5 + 0.5;
  }

  /** A lantern on a bracket: its iron cage and a cold white light (a glow, no scene light). */
  protected bracketLamp(kit: Kit, p: THREE.Vector3, h: number, color = WHITE, a = 0.7): void {
    const box = new THREE.BoxGeometry(1, 1, 1);
    kit.add(box, '#1c1f24', place(p.x, p.y + h, p.z, Math.PI / 4, new THREE.Vector3(0.16, 0.22, 0.16)));
    kit.add(box, '#e8eef8', place(p.x, p.y + h, p.z, Math.PI / 4, new THREE.Vector3(0.11, 0.15, 0.11)));
    box.dispose();
    this.light(new THREE.Vector3(p.x, p.y + h, p.z), color, 1.7, a, 1);
  }

  /**
   * A house of Musgoviejo on a cell (its face toward `face`, world yaw): a stone footing, plaster walls between dark
   * timbers, a steep slate roof with snow on it, a chimney, a shuttered window; `tall` storeys.
   */
  protected house(kit: Kit, p: THREE.Vector3, yaw: number, k: number, tall: number, lit: boolean): void {
    const box = new THREE.BoxGeometry(1, 1, 1);
    const w = S * (1.02 + k * 0.08);
    const d = S * 1.04;
    const hgt = 0.62 + tall * 0.5;
    const pl = PLASTER[Math.floor(k * 2.99)] as string;
    const tb = TIMBER[Math.floor((k * 7) % 2.99)] as string;
    // Footing and walls.
    kit.add(box, '#4a4e55', place(p.x, p.y + 0.18, p.z, yaw, new THREE.Vector3(w + 0.06, 0.36, d + 0.06)));
    kit.add(box, pl, place(p.x, p.y + 0.36 + hgt * 0.5, p.z, yaw, new THREE.Vector3(w, hgt, d)));
    // The timbers: corner posts, a beam at each floor, a brace across the face.
    const fx = Math.sin(yaw);
    const fz = Math.cos(yaw);
    const sx = Math.cos(yaw);
    const sz = -Math.sin(yaw);
    for (const s0 of [-1, 1]) kit.add(box, tb, place(p.x + sx * s0 * w * 0.5 + fx * d * 0.5, p.y + 0.36 + hgt * 0.5, p.z + sz * s0 * w * 0.5 + fz * d * 0.5, yaw, new THREE.Vector3(0.1, hgt, 0.1)));
    for (let f = 0; f <= tall; f++) kit.add(box, tb, place(p.x + fx * (d * 0.5 + 0.01), p.y + 0.4 + f * 0.5 + (f === tall ? hgt - tall * 0.5 - 0.08 : 0), p.z + fz * (d * 0.5 + 0.01), yaw, new THREE.Vector3(w, 0.08, 0.04)));
    kit.add(box, tb, place(p.x + fx * (d * 0.5 + 0.015), p.y + 0.36 + hgt * 0.45, p.z + fz * (d * 0.5 + 0.015), yaw, new THREE.Vector3(0.07, hgt * 0.9, 0.035), 0, 0.7 * (k > 0.5 ? 1 : -1)));
    // A shuttered window (two dark boards), or a cold one lit behind its shutters.
    const wy = p.y + 0.36 + Math.min(hgt - 0.22, 0.62);
    for (const s0 of [-0.22, 0.22]) {
      const wx = p.x + sx * s0 * w + fx * (d * 0.5 + 0.03);
      const wz = p.z + sz * s0 * w + fz * (d * 0.5 + 0.03);
      kit.add(box, lit && s0 > 0 ? '#c8d6ee' : '#1d1915', place(wx, wy, wz, yaw, new THREE.Vector3(0.22, 0.3, 0.03)));
      kit.add(box, tb, place(wx, wy + 0.17, wz, yaw, new THREE.Vector3(0.3, 0.05, 0.05)));
    }
    if (lit) this.light(new THREE.Vector3(p.x + sx * 0.22 * w + fx * (d * 0.5 + 0.12), wy, p.z + sz * 0.22 * w + fz * (d * 0.5 + 0.12)), COLD, 0.9, 0.45, 0.5);
    // The roof: two slabs pitched along the face, snow over their tops, a ridge.
    const ry = p.y + 0.36 + hgt;
    const pitch = 0.62;
    for (const s0 of [-1, 1]) {
      const cx = p.x + fx * s0 * d * 0.27;
      const cz = p.z + fz * s0 * d * 0.27;
      kit.add(box, ROOF[Math.floor(k * 4.99)] as string, place(cx, ry + 0.26, cz, yaw, new THREE.Vector3(w + 0.22, 0.08, d * 0.62), s0 * pitch, 0));
      // Snow lies up by the ridge (the slates show below it), and only on the side the wind left it.
      if ((s0 > 0 && k > 0.3) || k > 0.7) {
        const sx2 = p.x + fx * s0 * d * 0.12;
        const sz2 = p.z + fz * s0 * d * 0.12;
        kit.add(box, SNOW[Math.floor((k * 5) % 2.99)] as string, place(sx2, ry + 0.44, sz2, yaw, new THREE.Vector3(w + 0.1, 0.05, d * (0.14 + k * 0.1)), s0 * pitch, 0));
      }
    }
    kit.add(box, SNOW[0] as string, place(p.x, ry + 0.5, p.z, yaw, new THREE.Vector3(w + 0.2, 0.08, 0.14)));
    // The gable ends, plaster.
    const gable = new THREE.CylinderGeometry(0.0, 0.5, 1, 3, 1);
    for (const s0 of [-1, 1]) kit.add(gable, pl, place(p.x + sx * s0 * w * 0.5, ry + 0.24, p.z + sz * s0 * w * 0.5, yaw, new THREE.Vector3(0.06, 0.48, d * 0.58), 0, Math.PI / 2));
    gable.dispose();
    // A chimney now and then.
    if (k > 0.45) {
      const cx = p.x + sx * w * 0.28 - fx * d * 0.18;
      const cz = p.z + sz * w * 0.28 - fz * d * 0.18;
      kit.add(box, '#50545b', place(cx, ry + 0.55, cz, yaw, new THREE.Vector3(0.22, 0.6, 0.22)));
      kit.add(box, SNOW[1] as string, place(cx, ry + 0.87, cz, yaw, new THREE.Vector3(0.26, 0.05, 0.26)));
    }
    box.dispose();
  }

  /** The face of a house cell: toward its open neighbours. */
  protected faceOf(q: number, r: number, open: Set<string>): number | null {
    let fx = 0;
    let fz = 0;
    let n = 0;
    for (const [dq, dr] of [[1, 0], [1, -1], [0, -1], [-1, 0], [-1, 1], [0, 1]] as const) {
      if (!open.has(`${q + dq},${r + dr}`)) continue;
      const c = hexToWorld(dq, dr);
      fx += c.x;
      fz += c.y;
      n++;
    }
    return n ? Math.atan2(fx, fz) : null;
  }

  /* ---------- the lower street ---------- */

  private streetCraft: StreetCraft | null = null;
  private craftKit: Kit | null = null;

  /** The street's craft for this kit (camino-street.ts): one per build. */
  protected craft(kit: Kit): StreetCraft {
    if (this.craftKit !== kit || !this.streetCraft) {
      this.streetCraft?.dispose();
      this.streetCraft = new StreetCraft(kit, { hex: (q, r, h) => this.hex(q, r, h), k: (q, r, salt) => this.k(q, r, salt), light: (p, c, size, a, flick) => this.light(p, c, size, a, flick) });
      this.craftKit = kit;
    }
    return this.streetCraft;
  }

  /**
   * The houses on the rock cells: the fronts on the lane each of its own kind (camino-street.ts), now and then a
   * little square in their place; behind them the backs, and deeper in the yards among them (whole blocks of three
   * cells, so they read as yards and not as gaps).
   */
  protected houses(kit: Kit, rocks: { q: number; r: number }[], open: Set<string>): void {
    const C = this.craft(kit);
    const rock = new Set(rocks.map((h) => `${h.q},${h.r}`));
    // How deep each rock cell is from the lane (1: a front).
    const depth = new Map<string, number>();
    let ring: { q: number; r: number }[] = [];
    for (const h of rocks)
      if (this.faceOf(h.q, h.r, open) !== null) {
        depth.set(`${h.q},${h.r}`, 1);
        ring.push(h);
      }
    for (let dd = 2; dd <= 4 && ring.length; dd++) {
      const next: { q: number; r: number }[] = [];
      for (const h of ring)
        for (const [dq, dr] of [[1, 0], [1, -1], [0, -1], [-1, 0], [-1, 1], [0, 1]] as const) {
          const key = `${h.q + dq},${h.r + dr}`;
          if (!rock.has(key) || depth.has(key)) continue;
          depth.set(key, dd);
          next.push({ q: h.q + dq, r: h.r + dr });
        }
      ring = next;
    }
    let sinceSquare = 3;
    for (const h of rocks) {
      const key = `${h.q},${h.r}`;
      const dd = depth.get(key) ?? 5;
      const p = this.hex(h.q, h.r);
      const k = this.k(h.q, h.r);
      const k2 = this.k(h.q, h.r, 21);
      if (dd === 1) {
        const face = this.faceOf(h.q, h.r, open) as number;
        // Snap the face to the hex's sides so the fronts line up along the lane.
        const yaw = Math.round(face / (Math.PI / 3)) * (Math.PI / 3);
        // A little square instead of a house, every so often along the lane (never two near each other).
        sinceSquare++;
        if (k2 > 0.86 && sinceSquare > 6 && Math.abs(h.r) < this.map.radius - 3) {
          sinceSquare = 0;
          C.plaza(p, yaw, k);
          continue;
        }
        C.front(p, yaw, k, k2, k > 0.55 ? 2 : 1, k > 0.8);
        continue;
      }
      const yaw = (Math.floor(k * 6) % 6) * (Math.PI / 3);
      // Deeper in: yards by blocks (a coarse grid), the rest the backs of houses.
      const blk = this.k(Math.floor((h.q + 40) / 3), Math.floor((h.r + 40) / 3), 31);
      if (dd >= 3 && blk < 0.45) {
        const walls: number[] = [];
        for (let i = 0; i < 6; i++) {
          const [dq, dr] = ([[1, 0], [0, 1], [-1, 1], [-1, 0], [0, -1], [1, -1]] as const)[i] as readonly [number, number];
          const nb = this.k(Math.floor((h.q + dq + 40) / 3), Math.floor((h.r + dr + 40) / 3), 31);
          if (!(nb < 0.45 && (depth.get(`${h.q + dq},${h.r + dr}`) ?? 5) >= 3) && i % 2 === 0) walls.push(i);
        }
        // (One thing to a yard: only its first cell gets it, the others snow and walls.)
        const first = (h.q + 40) % 3 === 1 && (h.r + 40) % 3 === 1;
        C.yard(p.clone().setY(p.y - 0.05), k, first ? k2 : 0.99, walls);
        continue;
      }
      if ((dd >= 3 && k < 0.3) || (dd === 2 && k < 0.12)) continue;
      C.back(p.clone().setY(p.y - 0.06 * (dd - 1)), yaw, k, k2);
    }
  }

  /** The cobbles of a lane: worn slabs, snow banked at their edges (drawn on the cells, low). */
  protected cobbles(kit: Kit, cells: { q: number; r: number }[]): void {
    // (Nine sides: six-sided slabs read as the board's hexes, docs/RENDIMIENTO.md.)
    const slab = new THREE.CylinderGeometry(0.5, 0.52, 1, 9);
    for (const h of cells) {
      const p = this.hex(h.q, h.r);
      const k = this.k(h.q, h.r, 3);
      // A few loose stones and a drift of snow against a doorstep.
      if (k > 0.72) kit.add(slab, SNOW[Math.floor(k * 2.99)] as string, place(p.x + (k - 0.5) * 0.6, p.y + 0.02, p.z - 0.3, k * 4, new THREE.Vector3(0.5, 0.05, 0.35)));
      if (k < 0.12) kit.add(slab, '#5a5f67', place(p.x - 0.2, p.y + 0.03, p.z + 0.2, k * 9, new THREE.Vector3(0.18, 0.06, 0.14)));
    }
    slab.dispose();
  }

  protected buildStreet(kit: Kit, s: BattleState): void {
    const L = streetLayout(s.seed);
    this.St = L;
    const open = new Set<string>([...L.lane, ...L.alleys].map((h) => `${h.q},${h.r}`));
    this.houses(kit, L.rocks, open);
    this.cobbles(kit, L.lane);
    // The square at the foot: a stone trough frozen over, a cart without its mule, a lamp post.
    const box = new THREE.BoxGeometry(1, 1, 1);
    const foot = this.ctx.at(L.start.x, L.start.y, 0);
    kit.add(box, '#5e636b', place(foot.x - 1.9, foot.y + 0.24, foot.z + 0.2, 0.2, new THREE.Vector3(1.2, 0.48, 0.5)));
    kit.add(box, '#9fb2cc', place(foot.x - 1.9, foot.y + 0.49, foot.z + 0.2, 0.2, new THREE.Vector3(1.08, 0.03, 0.4)));
    kit.add(box, '#4a3c30', place(foot.x + 2.1, foot.y + 0.42, foot.z + 0.1, -0.4, new THREE.Vector3(0.9, 0.12, 1.5)));
    kit.add(box, SNOW[2] as string, place(foot.x + 2.1, foot.y + 0.5, foot.z + 0.1, -0.4, new THREE.Vector3(0.8, 0.05, 1.3)));
    for (const s0 of [-1, 1]) kit.add(box, '#4a3c30', place(foot.x + 2.1 + Math.sin(-0.4) * s0 * 0.7, foot.y + 0.62, foot.z + 0.1 + Math.cos(-0.4) * s0 * 0.7, -0.4, new THREE.Vector3(0.9, 0.3, 0.06)));
    const wheel = new THREE.CylinderGeometry(0.3, 0.3, 0.06, 10);
    for (const s0 of [-1, 1]) kit.add(wheel, TIMBER[0] as string, place(foot.x + 2.1 + s0 * 0.48 * Math.cos(-0.4), foot.y + 0.3, foot.z + 0.1 - s0 * 0.48 * Math.sin(-0.4), -0.4, 1, 0, Math.PI / 2));
    wheel.dispose();
    kit.add(box, '#2c3036', place(foot.x + 1.2, foot.y + 1.0, foot.z - 1.1, 0, new THREE.Vector3(0.08, 2.0, 0.08)));
    this.bracketLamp(kit, new THREE.Vector3(foot.x + 1.2, foot.y, foot.z - 1.1), 2.0, COLD, 0.6);
    // The village's arch at the top: a round arch in a stretch of the old wall, the shrine's lamp over its keystone.
    box.dispose();
    this.craft(kit).arch(this.ctx.at(L.arch.x, L.arch.y, 0));
    // The alleys: a lamp-less dark gap; a drift of snow at their mouths.
    for (const d of L.dens) this.stains.push(this.ctx.at(d.x, d.y, 0.05));
  }

  /** The opening's street (the cinematic): a straight lane between houses, a lamp at the far end. */
  protected buildSceneStreet(kit: Kit): void {
    const rocks = this.map.rocks ?? [];
    const open = new Set<string>(allCells(this.map.radius).filter((h) => !rocks.some((r) => r.q === h.q && r.r === h.r)).map((h) => `${h.q},${h.r}`));
    this.houses(kit, rocks.filter((h) => Math.max(Math.abs(h.q), Math.abs(h.r), Math.abs(h.q + h.r)) <= this.map.radius - 1), open);
    this.cobbles(kit, allCells(6).filter((h) => open.has(`${h.q},${h.r}`)));
    this.bracketLamp(kit, this.hex(0, -6).add(new THREE.Vector3(0.9, 0, 0)), 1.9, COLD, 0.7);
  }

  /* ---------- the chapel at the crossroads ---------- */

  /** Low dry-stone walls along a row of cells (the terraces), snow on their tops. */
  protected terraceWall(kit: Kit, cells: { q: number; r: number }[]): void {
    const box = new THREE.BoxGeometry(1, 1, 1);
    for (const h of cells) {
      const p = this.hex(h.q, h.r);
      const k = this.k(h.q, h.r, 5);
      for (let i = 0; i < 3; i++) {
        const x = p.x - 0.42 + i * 0.42 + (k - 0.5) * 0.1;
        kit.add(box, DRY[(i + Math.floor(k * 3)) % 3] as string, place(x, p.y + 0.2, p.z + 0.48, k + i, new THREE.Vector3(0.44, 0.4, 0.34)));
        kit.add(box, SNOW[i % 3] as string, place(x, p.y + 0.42, p.z + 0.48, k + i, new THREE.Vector3(0.42, 0.06, 0.32)));
      }
    }
    box.dispose();
  }

  /** The chapel: pale stone, a steep slate roof, a bell gable, its door under the lamp (the door is its own group). */
  protected chapelBody(kit: Kit, cells: { q: number; r: number }[], lampQ: number, lampR: number): void {
    const box = new THREE.BoxGeometry(1, 1, 1);
    let minX = Infinity;
    let maxX = -Infinity;
    let minZ = Infinity;
    let maxZ = -Infinity;
    for (const h of cells) {
      const p = this.hex(h.q, h.r);
      minX = Math.min(minX, p.x - 0.6);
      maxX = Math.max(maxX, p.x + 0.6);
      minZ = Math.min(minZ, p.z - 0.6);
      maxZ = Math.max(maxZ, p.z + 0.5);
    }
    const lamp = this.hex(lampQ, lampR);
    const cx = (minX + maxX) / 2;
    const cz = (minZ + maxZ) / 2;
    const w = maxX - minX;
    const d = maxZ - minZ;
    const y0 = lamp.y;
    // The walls, courses of pale stone; buttresses at the corners.
    for (let i = 0; i < 5; i++) kit.add(box, STONE[i % 3] as string, place(cx, y0 + 0.3 + i * 0.5, cz, 0, new THREE.Vector3(w - (i % 2) * 0.04, 0.5, d - (i % 2) * 0.04)));
    for (const sx of [-1, 1]) kit.add(box, STONE[1] as string, place(cx + sx * (w / 2 - 0.1), y0 + 1.0, maxZ - 0.1, 0, new THREE.Vector3(0.5, 2.0, 0.5)));
    // The roof: two steep slate slabs, snow down them, a ridge.
    const ry = y0 + 2.55;
    for (const sx of [-1, 1]) {
      kit.add(box, SLATE[1] as string, place(cx + sx * w * 0.26, ry + 0.9, cz, 0, new THREE.Vector3(w * 0.62, 0.12, d + 0.3), 0, sx * -0.95));
      kit.add(box, SNOW[sx > 0 ? 0 : 2] as string, place(cx + sx * w * 0.25, ry + 0.97, cz, 0, new THREE.Vector3(w * 0.5, 0.06, d + 0.2), 0, sx * -0.95));
    }
    const gable = new THREE.CylinderGeometry(0.0, 0.5, 1, 3, 1);
    kit.add(gable, STONE[0] as string, place(cx, ry + 0.75, maxZ - 0.02, 0, new THREE.Vector3(w * 1.0, 1.5, 0.12), Math.PI / 2, 0));
    gable.dispose();
    // The bell gable over the front, an empty arch, snow on its top; a small iron cross of the Order.
    kit.add(box, STONE[2] as string, place(cx, ry + 2.0, maxZ - 0.15, 0, new THREE.Vector3(0.7, 1.0, 0.3)));
    kit.add(box, '#141619', place(cx, ry + 1.95, maxZ - 0.0, 0, new THREE.Vector3(0.36, 0.5, 0.06)));
    kit.add(box, SNOW[0] as string, place(cx, ry + 2.53, maxZ - 0.15, 0, new THREE.Vector3(0.76, 0.06, 0.36)));
    kit.add(box, '#2c3036', place(cx, ry + 2.85, maxZ - 0.15, 0, new THREE.Vector3(0.04, 0.5, 0.04)));
    kit.add(box, '#2c3036', place(cx, ry + 2.95, maxZ - 0.15, 0, new THREE.Vector3(0.24, 0.04, 0.04)));
    // The doorway: a dark recess, a stone arch over it; the lamp's iron bracket above.
    kit.add(box, '#0c0d10', place(lamp.x, y0 + 0.75, maxZ + 0.0, 0, new THREE.Vector3(0.95, 1.5, 0.12)));
    kit.add(box, STONE[2] as string, place(lamp.x, y0 + 1.6, maxZ + 0.05, 0, new THREE.Vector3(1.3, 0.24, 0.2)));
    kit.add(box, '#2c3036', place(lamp.x, y0 + 2.1, maxZ + 0.25, 0, new THREE.Vector3(0.05, 0.05, 0.5)));
    // The step and the stones round it, worn.
    kit.add(box, STONE[0] as string, place(lamp.x, y0 + 0.06, maxZ + 0.35, 0, new THREE.Vector3(1.4, 0.12, 0.5)));
    box.dispose();
    this.lampAt = new THREE.Vector3(lamp.x, y0 + 1.95, maxZ + 0.42);
    // The door: two leaves of dark oak with iron straps, hinged at the jambs.
    const door = new THREE.Group();
    door.position.set(lamp.x, y0, maxZ + 0.08);
    const oak = this.track(new THREE.MeshStandardMaterial({ color: '#2e2620', roughness: 0.85, flatShading: true }));
    const iron = this.track(new THREE.MeshStandardMaterial({ color: '#2a2d32', roughness: 0.5, metalness: 0.4 }));
    const leafG = this.track(new THREE.BoxGeometry(0.44, 1.42, 0.08));
    const strapG = this.track(new THREE.BoxGeometry(0.42, 0.05, 0.02));
    for (const s0 of [-1, 1]) {
      const hinge = new THREE.Group();
      hinge.position.set(s0 * 0.46, 0, 0);
      const leaf = new THREE.Mesh(leafG, oak);
      leaf.position.set(-s0 * 0.23, 0.72, 0);
      leaf.castShadow = true;
      hinge.add(leaf);
      for (const y of [0.35, 1.1]) {
        const st = new THREE.Mesh(strapG, iron);
        st.position.set(-s0 * 0.23, y, 0.05);
        hinge.add(st);
      }
      hinge.userData.side = s0;
      door.add(hinge);
    }
    this.door = door;
    this.group.add(door);
  }

  /** The lamp's sconce: dark glass in an iron cage, lit by the beam. */
  protected sconce(kit: Kit): void {
    if (!this.lampAt) return;
    const p = this.lampAt;
    const box = new THREE.BoxGeometry(1, 1, 1);
    kit.add(box, '#1c1f24', place(p.x, p.y, p.z, Math.PI / 4, new THREE.Vector3(0.24, 0.32, 0.24)));
    kit.add(box, '#39404d', place(p.x, p.y, p.z, Math.PI / 4, new THREE.Vector3(0.17, 0.24, 0.17)));
    kit.add(box, '#1c1f24', place(p.x, p.y + 0.22, p.z, Math.PI / 4, new THREE.Vector3(0.12, 0.1, 0.12)));
    box.dispose();
  }

  /** Mother Aud's inscription: a stele of pale stone by the mirror, its lines cut. */
  protected stele(kit: Kit, p: THREE.Vector3): void {
    const box = new THREE.BoxGeometry(1, 1, 1);
    kit.add(box, STONE[2] as string, place(p.x, p.y + 0.42, p.z, 0.15, new THREE.Vector3(0.46, 0.84, 0.16)));
    kit.add(box, SNOW[0] as string, place(p.x, p.y + 0.86, p.z, 0.15, new THREE.Vector3(0.48, 0.05, 0.18)));
    for (let i = 0; i < 4; i++) kit.add(box, '#3c4048', place(p.x + Math.sin(0.15) * 0.085, p.y + 0.66 - i * 0.12, p.z + Math.cos(0.15) * 0.085, 0.15, new THREE.Vector3(0.3 - (i % 2) * 0.08, 0.025, 0.01)));
    box.dispose();
  }

  /** A hook for the lantern: an iron post, a curved arm, its hook; returns where the lantern hangs. */
  protected hookPost(kit: Kit, p: THREE.Vector3, yaw: number): THREE.Vector3 {
    const box = new THREE.BoxGeometry(1, 1, 1);
    kit.add(box, '#3a3f46', place(p.x, p.y + 0.12, p.z, 0, new THREE.Vector3(0.36, 0.24, 0.36)));
    kit.add(box, '#2a2e34', place(p.x, p.y + 0.95, p.z, 0, new THREE.Vector3(0.08, 1.9, 0.08)));
    const ax = Math.sin(yaw) * 0.32;
    const az = Math.cos(yaw) * 0.32;
    kit.add(box, '#2a2e34', place(p.x + ax * 0.5, p.y + 1.86, p.z + az * 0.5, yaw, new THREE.Vector3(0.05, 0.05, 0.36)));
    const hook = new THREE.TorusGeometry(0.06, 0.015, 4, 8, Math.PI * 1.4);
    kit.add(hook, '#2a2e34', place(p.x + ax, p.y + 1.78, p.z + az, yaw + Math.PI / 2));
    hook.dispose();
    box.dispose();
    return new THREE.Vector3(p.x + ax, p.y + 1.5, p.z + az);
  }

  /** A mirror on its stand: a stone foot, a wooden post, the iron yoke and the steel disc (the disc turns). */
  protected mirrorStand(kit: Kit, p: THREE.Vector3): MirrorView {
    const box = new THREE.BoxGeometry(1, 1, 1);
    kit.add(box, STONE[0] as string, place(p.x, p.y + 0.14, p.z, 0.3, new THREE.Vector3(0.62, 0.28, 0.62)));
    kit.add(box, SNOW[1] as string, place(p.x, p.y + 0.29, p.z, 0.3, new THREE.Vector3(0.6, 0.04, 0.6)));
    box.dispose();
    const g = new THREE.Group();
    g.position.copy(p);
    const wood = this.track(new THREE.MeshStandardMaterial({ color: '#3a322b', roughness: 0.85, flatShading: true }));
    const iron = this.track(new THREE.MeshStandardMaterial({ color: '#33373d', roughness: 0.45, metalness: 0.45 }));
    // Steel without a sky to show is black (docs/RENDIMIENTO.md): low metalness and a cold emissive so it reads.
    const steel = this.track(new THREE.MeshStandardMaterial({ color: '#c9d2de', roughness: 0.18, metalness: 0.3, emissive: '#5a6680', emissiveIntensity: 0.55 }));
    const post = new THREE.Mesh(this.track(new THREE.CylinderGeometry(0.06, 0.07, 1.0, 6)), wood);
    post.position.y = 0.78;
    g.add(post);
    const disc = new THREE.Group();
    disc.position.y = 1.25;
    const yoke = new THREE.Mesh(this.track(new THREE.TorusGeometry(0.4, 0.035, 5, 18, Math.PI)), iron);
    yoke.rotation.z = Math.PI;
    disc.add(yoke);
    const plate = new THREE.Mesh(this.track(new THREE.CylinderGeometry(0.34, 0.34, 0.05, 20)), steel);
    plate.rotation.x = Math.PI / 2;
    plate.castShadow = true;
    disc.add(plate);
    const rim = new THREE.Mesh(this.track(new THREE.TorusGeometry(0.345, 0.03, 5, 20)), iron);
    disc.add(rim);
    const back = new THREE.Mesh(this.track(new THREE.CylinderGeometry(0.3, 0.3, 0.02, 16)), iron);
    back.rotation.x = Math.PI / 2;
    back.position.z = -0.035;
    disc.add(back);
    g.add(disc);
    this.group.add(g);
    return { g, disc, yaw: 0, want: 0, at: new THREE.Vector3(p.x, p.y + 1.25, p.z), spin: 0 };
  }

  protected buildChapel(kit: Kit, s: BattleState): void {
    const L = chapelLayout(s.seed);
    this.C = L;
    this.terraceWall(kit, L.terraces);
    this.chapelBody(kit, L.chapel, L.lamp.q, L.lamp.r);
    this.sconce(kit);
    // The hooks, their arms toward where their beam leaves; the mirrors; Aud's stele by the first mirror.
    L.hooks.forEach((h) => {
      const d = hexToWorld([1, 1, 0, -1, -1, 0][h.dir] as number, [0, -1, -1, 0, 1, 1][h.dir] as number);
      this.hookTop.push(this.hookPost(kit, this.hex(h.q, h.r), Math.atan2(d.x, d.y)));
    });
    this.mirrors = L.mirrors.map((m) => this.mirrorStand(kit, this.hex(m.q, m.r)));
    const m0 = L.mirrors[0];
    if (m0) {
      const st = L.mirrorStand[0];
      const sp = st ? this.ctx.at(st.x, st.y, 0) : this.hex(m0.q, m0.r + 1);
      const mp = this.hex(m0.q, m0.r);
      this.stele(kit, mp.clone().lerp(sp, 0.55).add(new THREE.Vector3(0.45, 0, 0)));
    }
    this.terraceDress(kit, new Set(L.rocks.map((h) => `${h.q},${h.r}`)));
    this.buildBeam();
    // The lantern of the stretcher (the chapel's model leaves it off its pole): drawn where it hangs.
    this.lamp = this.lanternGroup();
    this.group.add(this.lamp);
  }

  /** Snowy shrubs, a cairn, a sheep pen's corner: the terraces' dressing, clear of what blocks. */
  protected terraceDress(kit: Kit, blocked: Set<string>): void {
    const ico = new THREE.IcosahedronGeometry(0.5, 0);
    const box = new THREE.BoxGeometry(1, 1, 1);
    for (const h of allCells(this.map.radius - 2)) {
      if (blocked.has(`${h.q},${h.r}`)) continue;
      const k = this.k(h.q, h.r, 9);
      const d = Math.max(Math.abs(h.q), Math.abs(h.r), Math.abs(h.q + h.r));
      if (d < this.map.radius - 3 || k < 0.6) continue;
      const p = this.hex(h.q, h.r);
      if (k > 0.86) {
        for (let i = 0; i < 3; i++) kit.add(ico, STONE[i] as string, place(p.x + (i - 1) * 0.1, p.y + 0.15 + i * 0.18, p.z, k + i, new THREE.Vector3(0.36 - i * 0.08, 0.26, 0.32 - i * 0.07)));
        kit.add(ico, SNOW[0] as string, place(p.x, p.y + 0.62, p.z, k, new THREE.Vector3(0.14, 0.06, 0.14)));
      } else {
        kit.add(ico, '#2e3a34', place(p.x + (k - 0.7) * 0.8, p.y + 0.2, p.z, k * 3, new THREE.Vector3(0.5, 0.36, 0.46)));
        kit.add(ico, SNOW[1] as string, place(p.x + (k - 0.7) * 0.8, p.y + 0.36, p.z, k * 3, new THREE.Vector3(0.44, 0.16, 0.4)));
      }
    }
    ico.dispose();
    box.dispose();
  }

  /** The campfire's scene in the chapel's lee: its front and door at the top, the terrace walls, a hook. */
  protected buildChapelScene(kit: Kit): void {
    const cells = (this.map.rocks ?? []).slice();
    this.chapelBody(kit, cells, 2, -3);
    this.sconce(kit);
    this.light(this.lampAt ?? new THREE.Vector3(), WHITE, 1.6, 0.75, 0.6);
    this.terraceWall(kit, allCells(6).filter((h) => h.r === 3 && Math.abs(h.q + h.r / 2) >= 2));
    this.terraceDress(kit, new Set(cells.map((h) => `${h.q},${h.r}`)));
    this.hookPost(kit, this.hex(-3, 0), 0.6);
    const m = this.mirrorStand(kit, this.hex(3, -1));
    m.disc.rotation.y = -0.8;
    this.stele(kit, this.hex(3, 0).add(new THREE.Vector3(-0.3, 0, 0)));
  }

  /* ---------- the slope ---------- */

  /**
   * The hill between the hairpins: no tiles, mounds of snow over rock that run into each other (several squashed blobs a
   * cell), higher toward the top of the board, dark rock breaking through here and there, and a dry-stone wall holding the
   * way's uphill side.
   */
  protected hill(kit: Kit, rocks: { q: number; r: number }[], open: Set<string>, R: number): void {
    const blob = new THREE.IcosahedronGeometry(0.5, 1);
    const ico = new THREE.IcosahedronGeometry(0.5, 0);
    const box = new THREE.BoxGeometry(1, 1, 1);
    for (const h of rocks) {
      const p = this.hex(h.q, h.r);
      const k = this.k(h.q, h.r, 2);
      const up = (R - h.r) / (2 * R);
      const tall = 0.45 + up * 1.3 + k * 0.3;
      for (let i = 0; i < 3; i++) {
        const a = k * 6.28 + i * 2.1;
        const x = p.x + Math.cos(a) * 0.35;
        const z = p.z + Math.sin(a) * 0.32;
        const sz = 1.25 + this.k(h.q, h.r, 3 + i) * 0.5;
        kit.add(blob, SNOW[(i + Math.floor(k * 3)) % 3] as string, place(x, p.y + tall * 0.35, z, a, new THREE.Vector3(sz * S, tall * 0.9, sz * S)));
      }
      // Rock breaking through on a few, big enough to read as rock (small ones read as holes from above).
      if (k > 0.85) kit.add(ico, STONE[Math.floor(k * 2.99)] as string, place(p.x + (k - 0.9) * 2.0, p.y + tall * 0.7, p.z - 0.2, k * 7, new THREE.Vector3(1.1, 0.7 + up * 0.5, 0.9)));
      // The wall holding the way, on the cells along its uphill side (the way is below them, to the south).
      if (open.has(`${h.q},${h.r + 1}`) || open.has(`${h.q - 1},${h.r + 1}`)) {
        for (let j = 0; j < 3; j++) {
          const x = p.x - 0.4 + j * 0.4;
          // (Pale stone, low, the snow wide on top: dark stones read as holes from the board's camera.)
          kit.add(box, STONE[(j + Math.floor(k * 3)) % 3] as string, place(x, p.y + 0.16, p.z + 0.55, k + j, new THREE.Vector3(0.44, 0.32, 0.26)));
          kit.add(box, SNOW[j % 3] as string, place(x, p.y + 0.34, p.z + 0.55, k + j, new THREE.Vector3(0.46, 0.07, 0.3)));
        }
      }
    }
    blob.dispose();
    ico.dispose();
    box.dispose();
  }

  /** A pine of the south face: three dark tiers, snow lying on each. */
  protected pine(kit: Kit, cone: THREE.BufferGeometry, x: number, y: number, z: number, h: number, k: number): void {
    kit.add(cone, '#2b211c', place(x, y + h * 0.08, z, 0, new THREE.Vector3(0.12, h * 0.16, 0.12)));
    for (let i = 0; i < 3; i++) {
      const r = h * (0.34 - i * 0.08);
      const ty = y + h * (0.22 + i * 0.24);
      kit.add(cone, PINE[(i + Math.floor(k * 3)) % 3] as string, place(x, ty + h * 0.16, z, k * 5 + i, new THREE.Vector3(r * 2, h * 0.36, r * 2)));
      kit.add(cone, SNOW[i % 3] as string, place(x, ty + h * 0.24, z, k * 5 + i, new THREE.Vector3(r * 1.55, h * 0.24, r * 1.55)));
    }
  }

  /** A cairn at a bend, and the ink on the snow there. */
  protected cairn(kit: Kit, p: THREE.Vector3): void {
    const ico = new THREE.IcosahedronGeometry(0.5, 0);
    for (let i = 0; i < 4; i++) kit.add(ico, STONE[i % 3] as string, place(p.x + (i % 2 ? 0.06 : -0.05), p.y + 0.16 + i * 0.2, p.z, i * 1.3, new THREE.Vector3(0.44 - i * 0.08, 0.26, 0.4 - i * 0.07)));
    kit.add(ico, SNOW[0] as string, place(p.x, p.y + 0.85, p.z, 0, new THREE.Vector3(0.16, 0.07, 0.16)));
    ico.dispose();
  }

  protected buildSlope(kit: Kit, s: BattleState): void {
    const L = bendsLayout(s.seed);
    this.B = L;
    const open = new Set<string>(L.lane.map((h) => `${h.q},${h.r}`));
    this.hill(kit, L.rocks, open, L.radius);
    for (const b of L.bends) {
      const p = this.ctx.at(b.x, b.y, 0);
      // The cairn on the outside of the bend, the ink where they come out.
      const out = p.clone().add(new THREE.Vector3(Math.sign(b.x) * 0.9, 0, 0));
      this.cairn(kit, out);
      this.stains.push(this.ctx.at(b.x, b.y, 0.04));
    }
    for (const d of L.dens) this.stains.push(this.ctx.at(d.x, d.y, 0.04));
    // The waymark at the top: a post with a board pointing up the cliff road, a snowy rock by it.
    const top = this.ctx.at(L.top.x, L.top.y, 0);
    const box = new THREE.BoxGeometry(1, 1, 1);
    kit.add(box, TIMBER[0] as string, place(top.x + 0.9, top.y + 0.8, top.z, 0, new THREE.Vector3(0.1, 1.6, 0.1)));
    kit.add(box, TIMBER[1] as string, place(top.x + 1.15, top.y + 1.4, top.z, 0.1, new THREE.Vector3(0.5, 0.16, 0.05)));
    box.dispose();
    this.bracketLamp(kit, new THREE.Vector3(top.x + 0.9, top.y, top.z), 1.7, COLD, 0.55);
  }

  /** The close's place: the cliff road along the top, the slope falling away below it. */
  protected buildSlopeTop(kit: Kit): void {
    const rocks = this.map.rocks ?? [];
    const open = new Set<string>(allCells(this.map.radius).filter((h) => !rocks.some((r) => r.q === h.q && r.r === h.r)).map((h) => `${h.q},${h.r}`));
    // (The slope drops below the road: lower to the south.)
    const prism = new THREE.CylinderGeometry(0.5, 0.58, 1, 6);
    for (const h of rocks) {
      const p = this.hex(h.q, h.r);
      const k = this.k(h.q, h.r, 4);
      const t = 0.25 + k * 0.3;
      kit.add(prism, DRY[Math.floor(k * 2.99)] as string, place(p.x, p.y - 0.6 + t * 0.5, p.z, k * 5, new THREE.Vector3(1.08 * S, t, 1.04 * S)));
      kit.add(prism, SNOW[Math.floor(k * 2.99)] as string, place(p.x, p.y - 0.6 + t + 0.03, p.z, k * 5, new THREE.Vector3(1.1 * S, 0.08, 1.06 * S)));
    }
    prism.dispose();
    this.cairn(kit, this.hex(3, -1));
    const box = new THREE.BoxGeometry(1, 1, 1);
    const w = this.hex(-3, -1);
    kit.add(box, TIMBER[0] as string, place(w.x, w.y + 0.8, w.z, 0, new THREE.Vector3(0.1, 1.6, 0.1)));
    kit.add(box, TIMBER[1] as string, place(w.x + 0.25, w.y + 1.4, w.z, 0.1, new THREE.Vector3(0.5, 0.16, 0.05)));
    box.dispose();
    this.bracketLamp(kit, w, 1.7, COLD, 0.55);
    void open;
  }

  /* ---------- the hall ---------- */

  protected buildHall(kit: Kit): void {
    const R = this.map.radius;
    const box = new THREE.BoxGeometry(1, 1, 1);
    // The walls: timber planks between posts, all round.
    for (const h of allCells(R)) {
      const d = Math.max(Math.abs(h.q), Math.abs(h.r), Math.abs(h.q + h.r));
      if (d !== 6) continue;
      const p = this.hex(h.q, h.r);
      const k = this.k(h.q, h.r, 6);
      const yaw = Math.atan2(-p.x, -p.z);
      kit.add(box, TIMBER[Math.floor(k * 2.99)] as string, place(p.x, p.y + 1.3, p.z, yaw, new THREE.Vector3(1.24 * S, 2.6, 0.3)));
      kit.add(box, '#4a3f36', place(p.x, p.y + 2.65, p.z, yaw, new THREE.Vector3(1.3 * S, 0.14, 0.44)));
    }
    // The long table, its benches, papers and a candle; the hearth against the north wall; the Order's old banner.
    const t = this.hex(0, -2);
    kit.add(box, '#4a3e34', place(t.x, t.y + 0.5, t.z, 0, new THREE.Vector3(3.0, 0.1, 0.9)));
    for (const [dx, dz] of [[-1.35, -0.35], [1.35, -0.35], [-1.35, 0.35], [1.35, 0.35]] as const) kit.add(box, '#3a3028', place(t.x + dx, t.y + 0.25, t.z + dz, 0, new THREE.Vector3(0.1, 0.5, 0.1)));
    for (const dz of [-0.8, 0.8]) kit.add(box, '#3a3028', place(t.x, t.y + 0.28, t.z + dz, 0, new THREE.Vector3(2.8, 0.08, 0.32)));
    for (let i = 0; i < 4; i++) kit.add(box, '#cfd3d8', place(t.x - 0.9 + i * 0.5, t.y + 0.56, t.z + (i % 2) * 0.12, i * 0.4, new THREE.Vector3(0.34, 0.01, 0.24)));
    for (const dx of [-0.6, 0.9]) {
      kit.add(box, '#dfe3ea', place(t.x + dx, t.y + 0.65, t.z - 0.1, 0, new THREE.Vector3(0.05, 0.18, 0.05)));
      this.light(new THREE.Vector3(t.x + dx, t.y + 0.8, t.z - 0.1), WARM, 0.5, 0.85, 1);
    }
    const f = this.hex(-2, -3);
    kit.add(box, '#4a4f57', place(f.x, f.y + 0.6, f.z, 0.5, new THREE.Vector3(1.6, 1.2, 0.8)));
    kit.add(box, '#120c0a', place(f.x + Math.sin(0.5) * 0.3, f.y + 0.45, f.z + Math.cos(0.5) * 0.3, 0.5, new THREE.Vector3(0.9, 0.5, 0.3)));
    this.light(new THREE.Vector3(f.x + Math.sin(0.5) * 0.5, f.y + 0.5, f.z + Math.cos(0.5) * 0.5), HEARTH, 2.2, 0.7, 0.6);
    const ban = this.hex(3, -4);
    kit.add(box, '#2a2e34', place(ban.x, ban.y + 2.2, ban.z, -0.6, new THREE.Vector3(1.0, 0.05, 0.05)));
    kit.add(box, '#5a2620', place(ban.x, ban.y + 1.6, ban.z, -0.6, new THREE.Vector3(0.8, 1.1, 0.03)));
    kit.add(box, '#d6dbe3', place(ban.x + 0.02, ban.y + 1.65, ban.z + 0.02, -0.6, new THREE.Vector3(0.24, 0.24, 0.03)));
    box.dispose();
  }

  /* ---------- the lantern, the beam ---------- */

  /** The stretcher's lantern as its own group (the chapel's): cage, glass, flame. */
  protected lanternGroup(): THREE.Group {
    const l = new THREE.Group();
    const iron = this.track(new THREE.MeshStandardMaterial({ color: '#2c3036', roughness: 0.5, metalness: 0.4 }));
    const glass = this.track(new THREE.MeshStandardMaterial({ color: '#e9eef6', roughness: 0.15, emissive: '#dfe8f6', emissiveIntensity: 1.1 }));
    const cap = new THREE.Mesh(this.track(new THREE.CylinderGeometry(0.06, 0.12, 0.08, 8)), iron);
    cap.position.y = 0.16;
    l.add(cap);
    const base = new THREE.Mesh(this.track(new THREE.CylinderGeometry(0.11, 0.09, 0.05, 8)), iron);
    base.position.y = -0.15;
    l.add(base);
    const body = new THREE.Mesh(this.track(new THREE.CylinderGeometry(0.085, 0.085, 0.26, 10)), glass);
    l.add(body);
    l.scale.setScalar(1.3);
    return l;
  }

  /** The lantern's warm pool on the ground and the faint ring at the edge of its light. */
  protected buildPool(alpha: number): void {
    const vert = /* glsl */ `varying vec2 vUv; void main() { vUv = uv; gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); }`;
    this.poolMat = this.track(
      new THREE.ShaderMaterial({
        uniforms: { uColor: { value: new THREE.Color('#ffd6ae') }, uA: { value: alpha } },
        vertexShader: vert,
        fragmentShader: /* glsl */ `
          uniform vec3 uColor; uniform float uA; varying vec2 vUv;
          void main() {
            float d = length(vUv - 0.5) * 2.0;
            float a = smoothstep(1.0, 0.15, d) * uA;
            gl_FragColor = vec4(uColor * a, a);
          }`,
        transparent: true,
        depthWrite: false,
        blending: THREE.AdditiveBlending,
      }),
    );
    this.edgeMat = this.track(
      new THREE.ShaderMaterial({
        uniforms: { uColor: { value: new THREE.Color('#f0f4ff') }, uA: { value: 0.42 }, uT: { value: 0 } },
        vertexShader: vert,
        fragmentShader: /* glsl */ `
          uniform vec3 uColor; uniform float uA; uniform float uT; varying vec2 vUv;
          void main() {
            vec2 p = vUv - 0.5;
            float d = length(p) * 2.0;
            float a = smoothstep(0.93, 0.97, d) * smoothstep(1.0, 0.975, d);
            // Broken a little, like snow lit at its edge.
            float br = 0.65 + 0.35 * sin(atan(p.y, p.x) * 18.0 + uT * 0.6);
            a *= uA * br;
            gl_FragColor = vec4(uColor * a, a);
          }`,
        transparent: true,
        depthWrite: false,
        blending: THREE.AdditiveBlending,
      }),
    );
    const disc = this.track(new THREE.PlaneGeometry(1, 1));
    this.pool = new THREE.Mesh(disc, this.poolMat);
    this.edge = new THREE.Mesh(disc, this.edgeMat);
    for (const m of [this.pool, this.edge]) {
      m.rotation.x = -Math.PI / 2;
      m.renderOrder = 3;
      m.frustumCulled = false;
      this.group.add(m);
    }
  }

  /** The beam's strip: quads along its corners, a white light on the snow. */
  protected buildBeam(): void {
    this.beamMat = this.track(
      new THREE.ShaderMaterial({
        uniforms: { uT: { value: 0 } },
        vertexShader: /* glsl */ `attribute float aU; attribute float aV; varying float vU; varying float vV;
          void main() { vU = aU; vV = aV; gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); }`,
        fragmentShader: /* glsl */ `uniform float uT; varying float vU; varying float vV;
          void main() {
            // A hard white core (a third of the strip) in a wide cold halo, with pulses running from the source: it has
            // to read on pale snow with the camera far back on a phone (a thin strip of white on white didn't).
            float x = abs(vV);
            float core = smoothstep(0.36, 0.12, x);
            float halo = (1.0 - x) * (1.0 - x);
            float pulse = smoothstep(0.55, 1.0, sin(vU * 1.6 - uT * 5.0));
            float run = 0.85 + 0.15 * sin(vU * 3.0 - uT * 6.0);
            vec3 c = mix(vec3(0.55, 0.72, 1.0), vec3(1.0), core);
            float a = (halo * 0.5 + core * 0.95 + pulse * halo * 0.45) * run;
            gl_FragColor = vec4(c * a, a);
          }`,
        transparent: true,
        depthWrite: false,
        blending: THREE.AdditiveBlending,
        side: THREE.DoubleSide,
      }),
    );
    const g = this.track(new THREE.BufferGeometry());
    this.beam = new THREE.Mesh(g, this.beamMat);
    this.beam.renderOrder = 4;
    this.beam.frustumCulled = false;
    this.group.add(this.beam);
  }

  /** The strip rebuilt from the beam's corners (sim units). */
  protected rebuildBeam(pts: number[]): void {
    if (!this.beam) return;
    const pos: number[] = [];
    const u: number[] = [];
    const v: number[] = [];
    const idx: number[] = [];
    let run = 0;
    // (Wide: the core is a third of it, the rest its halo; docs/RENDIMIENTO.md, the cleric's road.)
    const half = 0.62;
    for (let i = 0; i + 3 < pts.length; i += 2) {
      const a = this.ctx.at(pts[i] as number, pts[i + 1] as number, 0.09);
      const b = this.ctx.at(pts[i + 2] as number, pts[i + 3] as number, 0.09);
      const dx = b.x - a.x;
      const dz = b.z - a.z;
      const len = Math.hypot(dx, dz) || 1;
      const nx = (-dz / len) * half;
      const nz = (dx / len) * half;
      const k = pos.length / 3;
      pos.push(a.x + nx, a.y, a.z + nz, a.x - nx, a.y, a.z - nz, b.x + nx, b.y, b.z + nz, b.x - nx, b.y, b.z - nz);
      u.push(run, run, run + len, run + len);
      v.push(1, -1, 1, -1);
      idx.push(k, k + 1, k + 2, k + 1, k + 3, k + 2);
      run += len;
    }
    const g = this.beam.geometry;
    g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
    g.setAttribute('aU', new THREE.Float32BufferAttribute(u, 1));
    g.setAttribute('aV', new THREE.Float32BufferAttribute(v, 1));
    g.setIndex(idx);
    g.computeBoundingSphere();
  }

  /* ---------- the lantern's light ---------- */

  /** The board's one point light, made the lantern's (or the place's own: the hall's hearth, the scenes' lamps). */
  protected pl: THREE.PointLight | null = null;
  /** Its strength, flickering round it (the view's brazier flicker). */
  lightBase = 10;

  takeLight(f: THREE.PointLight): void {
    // (Left at the board's middle it tinted a house up the street orange: a place of its own always takes it.)
    this.pl = f;
    f.color.set(this.inside ? '#ff9a62' : '#ffdcb8');
    f.distance = this.inside ? 10 : 7.5;
    f.decay = 1.6;
    this.lightBase = this.inside ? 12 : this.place === 'musgoStreet' || this.place === 'musgoNight' ? 8 : 5;
    f.intensity = this.lightBase;
    // Where it stands in a scene (no stretcher moving): over the lane, by the hearth, by the fire, over the road.
    const at = this.place === 'musgoHall' ? this.hex(-2, -2, 0.8) : this.place === 'chapelFire' ? this.hex(0, 0, 1.0) : this.place === 'slopeTop' ? this.hex(0, 0, 1.5) : this.hex(0, 2, 1.6);
    f.position.copy(at);
  }

  /* ---------- the board, repainted ---------- */

  apply(terrain: TerrainBuild, hour: Hour = 'night'): void {
    this.terrain = terrain;
    const c = new THREE.Color();
    const col = terrain.mesh.geometry.getAttribute('color') as THREE.BufferAttribute | undefined;
    const pos = terrain.mesh.geometry.getAttribute('position') as THREE.BufferAttribute | undefined;
    // One ground, not hex tiles: the seams closed.
    smoothTiles(terrain.mesh.geometry, terrain.heights, this.map.radius, terrain.ground);
    const rock = new Set<string>();
    for (const h of this.map.rocks ?? []) rock.add(`${h.q},${h.r}`);
    const street = this.place === 'musgoStreet' || this.place === 'musgoNight';
    const board = this.map.radius - 2 + 0.5;
    // The rim's tiles laid down to the ground (they stood as a hex-toothed wall round the board, docs/RENDIMIENTO.md).
    if (pos && !this.inside) {
      terrain.mesh.updateMatrixWorld(true);
      const inv = terrain.mesh.matrixWorld.clone().invert();
      const base = this.ctx.ground(0, 0);
      const v = new THREE.Vector3();
      for (let i = 0; i < pos.count; i++) {
        v.fromBufferAttribute(pos, i).applyMatrix4(terrain.mesh.matrixWorld);
        const hq = v.x / S - v.z / (S * 0.866) / 2;
        const hr = v.z / (S * 0.866);
        if (Math.max(Math.abs(hq), Math.abs(hr), Math.abs(hq + hr)) < board + 0.2) continue;
        v.y = base;
        v.applyMatrix4(inv);
        pos.setXYZ(i, v.x, v.y, v.z);
      }
      pos.needsUpdate = true;
      terrain.mesh.geometry.computeVertexNormals();
    }
    const tint = (mesh: THREE.Mesh, out: boolean): void => {
      const geo = mesh.geometry;
      const cc = geo.getAttribute('color') as THREE.BufferAttribute | undefined;
      const pp = geo.getAttribute('position') as THREE.BufferAttribute | undefined;
      if (!cc || !pp) return;
      mesh.updateMatrixWorld(true);
      const v = new THREE.Vector3();
      const far = new THREE.Color();
      for (let i = 0; i < cc.count; i++) {
        v.fromBufferAttribute(pp, i).applyMatrix4(mesh.matrixWorld);
        const n = 0.5 + 0.5 * Math.sin(v.x * 1.7 + v.z * 0.9) * Math.cos(v.z * 1.3 - v.x * 0.5);
        const n2 = 0.5 + 0.5 * Math.sin(v.x * 0.43 - v.z * 0.61) * Math.sin(v.z * 0.37 + v.x * 0.22);
        if (this.inside) {
          // Floorboards, dark and worn.
          const plank = Math.floor(v.x * 2.2) % 2 === 0 ? 0.92 : 1.0;
          c.set('#3a332c').multiplyScalar(plank * (0.85 + n * 0.25));
          cc.setXYZ(i, c.r, c.g, c.b);
          continue;
        }
        if (street) {
          // Cobbles of the lane: grey slate, snow lying between them and banked at the edges.
          const snow = n2 > 0.55 || n > 0.82;
          c.set(snow ? (SNOW[Math.floor(n * 2.99)] as string) : n > 0.5 ? '#585e67' : '#4b5059').multiplyScalar(snow ? 0.92 : 0.95);
        } else if (this.place === 'lapaSlope' || this.place === 'slopeTop') {
          // The way: snow trodden grey by feet and mules, darker ruts in it (the mounds either side stay white).
          const rut = Math.abs(Math.sin(v.x * 3.1 + v.z * 0.4)) < 0.14;
          c.set(rut ? '#5d6674' : n > 0.55 ? '#7d8796' : '#717b8a').multiplyScalar(0.92 + n2 * 0.12);
        } else {
          // The terraces: snow, and the grass and stones showing through it here and there.
          const bare = n2 < 0.28 && n > 0.4;
          c.set(bare ? '#4d5a55' : (SNOW[Math.floor(n * 2.99)] as string)).multiplyScalar(0.9 + n2 * 0.1);
        }
        // Past the board's rim along a wandering line (never its hexagon): the snow of the hillside, a little darker
        // and bluer the farther out (the lantern's world ends at the dark).
        const rd = Math.hypot(v.x, v.z) / S + 0.6 * Math.sin(Math.atan2(v.z, v.x) * 5 + 1.3) + 0.35 * Math.sin(v.x * 0.4 - v.z * 0.3);
        const beyond = out ? 1 : Math.max(0, Math.min(1, (rd - (board - 1.6)) / 2.2));
        if (beyond > 0) {
          const dark = Math.max(0, Math.min(1, (rd - board) / 9));
          far.set(SNOW[Math.floor(n * 2.99)] as string).multiplyScalar(0.78 - dark * 0.3 + n2 * 0.08);
          c.lerp(far, beyond);
        }
        cc.setXYZ(i, c.r, c.g, c.b);
      }
      cc.needsUpdate = true;
    };
    void col;
    tint(terrain.mesh, false);
    terrain.decor.traverse((o) => {
      if (o.name === 'roads') o.visible = false;
      if (o.name === 'outskirts-ground') {
        if (this.inside) o.visible = false;
        else tint(o as THREE.Mesh, true);
      }
      // (None of the outskirts' scattered dressing: it read as black specks on the snow. The place puts its own pines.)
      if ((o as THREE.InstancedMesh).isInstancedMesh) o.visible = false;
    });
    if (terrain.fog) terrain.fog.group.visible = !this.inside;
    if (!this.inside) {
      this.dome = new SkyDome();
      this.group.add(this.dome.mesh);
      this.fall = new SnowFall(this.ctx.low ? 380 : 900, new THREE.Vector2(0.35, 0.15), 0.35);
      this.group.add(this.fall.points);
    }
    this.paint(hour);
  }

  paint(hour: Hour): void {
    const look = HOURS[this.inside ? 'inside' : hour];
    const scene = this.ctx.scene;
    scene.background = new THREE.Color(look.bg);
    if (scene.fog instanceof THREE.Fog) {
      scene.fog.color.set(look.fog[0]);
      scene.fog.near = look.fog[1];
      scene.fog.far = look.fog[2];
    }
    const { hemi, key, rim } = this.ctx;
    if (hemi) {
      hemi.color.set(look.hemi[0]);
      hemi.groundColor.set(look.hemi[1]);
      hemi.intensity = look.hemi[2];
    }
    if (key) {
      key.color.set(look.key[0]);
      key.intensity = look.key[1];
    }
    if (rim) {
      rim.color.set(look.rim[0]);
      rim.intensity = look.rim[1];
    }
    this.dome?.set(look.sky[0], look.sky[1], look.sky[2]);
    if (this.dome) this.dome.u.uSunDir.value.set(-0.5, hour === 'dawn' ? 0.06 : 0.55, -0.6);
    this.terrain?.fog?.tint({ low: look.fog[0], mid: look.fog[0], far: look.bg, alpha: 1 });
  }

  /* ---------- each tick and frame ---------- */

  tick(s: BattleState): void {
    for (const e of s._events ?? []) {
      if (e.t !== 'senda') continue;
      const p = this.ctx.at(e.x, e.y, 0.2);
      if (e.kind === 'limpetOut') {
        // Ink welling up out of the snow.
        this.ctx.vfx.burst(p, { n: 12, color: '#120c1c', speed: 1.4, up: 1.4, size: 0.18, life: 12, gravity: 1 });
        this.ctx.vfx.ring(p.clone().setY(p.y - 0.12), 0.2, 1.1, '#6a4aa8', 6, 0.8);
      } else if (e.kind === 'stick') {
        this.ctx.vfx.burst(p, { n: 6, color: '#1a1226', speed: 0.8, up: 0.6, size: 0.12, life: 8 });
      } else if (e.kind === 'peel') {
        // A holy blow peels it: a white flash off the canvas.
        this.ctx.flare(p.clone().setY(p.y + 0.5), '#ffffff', 1.6, 8);
        this.ctx.vfx.ring(p.clone().setY(p.y - 0.1), 0.2, 1.3, '#f4f8ff', 6, 0.9);
      } else if (e.kind === 'mended') {
        const t = s.senda?.npcs.find((n) => n.id === 'tessa');
        if (t) {
          const tp = this.ctx.at(t.x, t.y, 0.9);
          this.ctx.vfx.beam(p.clone().setY(p.y + 0.7), tp, '#e8f0ff', 3, 0.05);
          this.ctx.vfx.ring(tp.clone().setY(tp.y - 0.8), 0.3, 1.2, '#e8f0ff', 8, 0.7);
        }
      } else if (e.kind === 'set') {
        this.ctx.vfx.burst(p, { n: 10, color: '#c8d3e1', speed: 1.2, up: 0.8, size: 0.16, life: 10, smoke: true });
      } else if (e.kind === 'lifted') {
        this.ctx.vfx.ring(p.clone().setY(p.y - 0.15), 0.2, 1.0, '#e8f0ff', 6, 0.7);
      } else if (e.kind === 'hung') {
        this.ctx.flare(p.clone().setY(p.y + 1.4), '#f4f8ff', 1.4, 8);
        this.beamKey = '';
      } else if (e.kind === 'mirror') {
        const m = this.mirrors[e.n ?? 0];
        if (m) {
          m.spin = 1;
          this.ctx.vfx.burst(m.at, { n: 10, color: '#e8f0ff', speed: 1.6, up: 0.6, size: 0.08, life: 8 });
        }
        this.beamKey = '';
      } else if (e.kind === 'lamp') {
        // The lamp lit: a flash, the shake of it, the snow lit white round the door.
        this.lampLit = 1;
        this.flash = 1;
        this.doorWant = 1;
        if (this.lampAt) {
          this.ctx.flare(this.lampAt, '#ffffff', 4.2, 16);
          this.ctx.vfx.ring(this.lampAt.clone().setY(this.lampAt.y - 1.9), 0.4, 4.5, '#f4f8ff', 14, 0.9);
          this.ctx.vfx.burst(this.lampAt, { n: 26, color: '#ffffff', speed: 2.4, up: 1.2, size: 0.09, life: 14 });
        }
        this.ctx.shake(0.35);
      } else if (e.kind === 'end') {
        this.ctx.flare(p.clone().setY(p.y + 1.2), '#f4f8ff', 2.4, 12);
      }
    }
  }

  draw(s: BattleState): void {
    this.lastState = s;
    const w = s.senda?.litter;
    const r = s.rules?.senda?.litter;
    if (!w || !r) return;
    // The pool round the lantern, the size of its light; its edge where the stalkers start to show.
    const lp = lanternAt(s);
    if (lp && this.pl) {
      const hk = this.C && w.lantern >= 0 ? this.hookTop[w.lantern] : undefined;
      if (hk) this.pl.position.copy(hk);
      else this.pl.position.copy(this.ctx.at(lp.x, lp.y, 1.4));
    }
    if (lp && this.pool && this.edge) {
      const p = this.ctx.at(lp.x, lp.y, 0.06);
      const d = r.light * K * 2;
      this.pool.position.copy(p);
      this.pool.scale.set(d * 1.05, d * 1.05, 1);
      this.edge.position.copy(p).setY(p.y + 0.01);
      this.edge.scale.set(d, d, 1);
    }
    // The chapel's lantern on its hook, or on the stretcher's pole over Tessa.
    if (this.lamp && this.C) {
      const hk = w.lantern >= 0 ? this.hookTop[w.lantern] : undefined;
      if (hk) this.lamp.position.copy(hk);
      else {
        const t = s.senda?.npcs.find((n) => n.id === 'tessa');
        if (t) this.lamp.position.copy(this.ctx.at(t.x, t.y - 700, 1.08));
      }
    }
    // The beam, rebuilt when it changes.
    const key = w.beam.join(',');
    if (key !== this.beamKey) {
      this.beamKey = key;
      this.rebuildBeam(w.beam);
      this.aimMirrors(s);
    }
  }

  /**
   * Each mirror's disc, turned to send the beam where it goes: square to the bisector of where the beam comes from and
   * where it leaves (a mirror the beam doesn't reach stands by its position alone).
   */
  protected aimMirrors(s: BattleState): void {
    const w = s.senda?.litter;
    const L = this.C;
    if (!w || !L) return;
    const pts: THREE.Vector3[] = [];
    for (let i = 0; i + 1 < w.beam.length; i += 2) pts.push(this.ctx.at(w.beam[i] as number, w.beam[i + 1] as number, 0));
    L.mirrors.forEach((m, i) => {
      const view = this.mirrors[i];
      if (!view) return;
      const mp = this.hex(m.q, m.r);
      let set = false;
      for (let k = 1; k + 1 < pts.length; k++) {
        const p = pts[k] as THREE.Vector3;
        if (Math.hypot(p.x - mp.x, p.z - mp.z) > 0.1) continue;
        const a = (pts[k - 1] as THREE.Vector3).clone().sub(p).normalize();
        const b = (pts[k + 1] as THREE.Vector3).clone().sub(p).normalize();
        const n = a.add(b);
        // Back where it came from: the disc square to it.
        const dir = n.lengthSq() < 1e-4 ? (pts[k - 1] as THREE.Vector3).clone().sub(p) : n;
        view.want = Math.atan2(dir.x, dir.z);
        set = true;
        break;
      }
      if (!set) view.want = ((w.turns[i] ?? 0) * Math.PI) / 3 + i * 0.7;
    });
  }

  frame(now: number, camera?: THREE.Camera): void {
    const dt = this.now ? Math.min(0.1, now - this.now) : 0;
    this.now = now;
    this.dome?.frame(now, camera);
    this.fall?.frame(now, camera);
    if (this.edgeMat) (this.edgeMat.uniforms.uT as THREE.IUniform<number>).value = now;
    if (this.beamMat) (this.beamMat.uniforms.uT as THREE.IUniform<number>).value = now % 120;
    const s = this.lastState;
    const w = s?.senda?.litter;
    // The mirrors turn to where they are set (a quick swing, a little overshoot).
    for (const m of this.mirrors) {
      let d = m.want - m.yaw;
      while (d > Math.PI) d -= Math.PI * 2;
      while (d < -Math.PI) d += Math.PI * 2;
      m.yaw += d * Math.min(1, dt * 7);
      m.disc.rotation.y = m.yaw;
      m.spin = Math.max(0, m.spin - dt * 2);
    }
    // The door swings in once the lamp is lit.
    this.doorOpen += (this.doorWant - this.doorOpen) * Math.min(1, dt * 1.4);
    if (this.door)
      for (const c of this.door.children) {
        const side = (c.userData.side as number) ?? 1;
        c.rotation.y = side * this.doorOpen * 1.7;
      }
    this.flash = Math.max(0, this.flash - dt * 0.5);
    this.glow.begin();
    for (const l of this.lights) {
      const f = l.flick ? 0.86 + 0.14 * Math.sin(now * (5 + l.seed) + l.seed) * l.flick : 1;
      this.glow.add(l.p.x, l.p.y, l.p.z, l.c, l.size, l.a * f);
    }
    // The ink where the limpets come out, breathing violet.
    for (const p of this.stains) this.glow.add(p.x, p.y, p.z, INK, 1.4, 0.18 + 0.08 * Math.sin(now * 1.7 + p.x));
    if (w && s?.rules?.senda?.litter) {
      // The lantern's flame and its halo (on the stretcher's model, or the chapel's own lantern).
      const lp = lanternAt(s);
      if (lp) {
        const at = this.lamp && this.C ? this.lamp.position : this.ctx.at(lp.x, lp.y, 1.25);
        const fl = 0.9 + 0.1 * Math.sin(now * 9.3) + 0.05 * Math.sin(now * 23.1);
        this.glow.add(at.x, at.y, at.z, WHITE, 1.3, 0.95 * fl);
        this.glow.add(at.x, at.y, at.z, WARM, 3.0, 0.2 * fl);
      }
      // The limpets: a sick violet under each (red-rimmed while one is on the stretcher), so they read on the snow.
      for (const l of w.limpets) {
        const e = s.enemies.find((x) => x.id === l.id);
        if (!e) continue;
        const p = this.ctx.at(e.x, e.y, 0.12);
        this.glow.add(p.x, p.y, p.z, INK, 1.1, 0.55);
        if (l.on === 1) this.glow.add(p.x, p.y + 0.1, p.z, RED, 0.7, 0.35 + 0.2 * Math.sin(now * 6 + l.id));
      }
      // The beam's glints: at its source, at every mirror it turns at, where it stops.
      for (let i = 0; i + 1 < w.beam.length; i += 2) {
        const p = this.ctx.at(w.beam[i] as number, w.beam[i + 1] as number, i === 0 ? 1.5 : 1.25);
        const last = i + 2 >= w.beam.length;
        this.glow.add(p.x, p.y, p.z, WHITE, last ? 0.9 : 1.5, last ? 0.45 : 0.8 + 0.2 * Math.sin(now * 6 + i));
      }
    }
    // The lamp over the door: dark glass, then white and lighting the snow and the doorway.
    if (this.lampAt) {
      const lit = this.lampLit > 0 || (w?.lit ?? -1) >= 0;
      if (lit) {
        this.lampLit = 1;
        this.glow.add(this.lampAt.x, this.lampAt.y, this.lampAt.z, WHITE, 1.6, 1);
        this.glow.add(this.lampAt.x, this.lampAt.y, this.lampAt.z, COLD, 4 + this.flash * 5, 0.4 + this.flash * 0.5);
        this.glow.add(this.lampAt.x, this.lampAt.y - 1.2, this.lampAt.z - 0.3, WARM, 2.2 * this.doorOpen, 0.5 * this.doorOpen);
      } else if (this.C) {
        this.glow.add(this.lampAt.x, this.lampAt.y, this.lampAt.z, RED, 0.5, 0.25 + 0.15 * Math.sin(now * 2.4));
      }
    }
    this.frameOwn(now, dt);
    this.glow.end();
  }

  dispose(): void {
    for (const m of this.mats) m.dispose();
    for (const g of this.geos) g.dispose();
    this.dome?.dispose();
    this.fall?.dispose();
    this.glow.mesh.geometry.dispose();
    this.glow.material.dispose();
    this.group.removeFromParent();
  }
}
